import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { CaseStudyCard } from './CaseStudyCard';
import { useLanguage } from '../contexts/LanguageContext';
import { caseStudies } from '../data/caseStudies';
const EDGE = 'max(1.25rem, calc((100vw - 1140px) / 2))';
export function CaseStudies() {
  const {
    t
  } = useLanguage();
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateButtons = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateButtons();
    window.addEventListener('resize', updateButtons);
    return () => window.removeEventListener('resize', updateButtons);
  }, [updateButtons]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    const card = el?.querySelector('li');
    if (!el || !card) return;
    el.scrollBy({
      left: direction * (card.clientWidth + 24),
      behavior: 'smooth'
    });
  };
  return <section id="cas-clients" aria-labelledby="cases-title" className="scroll-mt-24 pb-20 pt-4 sm:pb-24 lg:pb-28" data-id="element-16" data-mp="33929eccnc2ba">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.5
    }} transition={{
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} className="px-5 text-center" data-id="element-17" data-mp="2za1u33tdnht1">
        <span className="inline-block rounded-md border border-[rgba(248,164,0,0.45)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-blue-reways-950-lock" data-id="element-18" data-mp="3m7woxnjxp6si">
          {t.cases.eyebrow}
        </span>
        <h2 id="cases-title" className="mt-4 font-hero text-[32px] font-medium leading-[1.15] text-blue-reways-950-lock sm:text-[40px]" data-id="element-19" data-mp="ikehp268gtgo">
          {t.cases.titleBefore}{' '}
          <span className="relative inline-block text-[#F2A007]" data-id="element-20" data-mp="2821q1m62o2o4">
            <span aria-hidden="true" className="absolute -inset-x-[0.14em] inset-y-[0.06em] rounded-[0.05em]" style={{
            background: 'linear-gradient(90deg, rgba(248,164,0,0.30) 0%, rgba(248,164,0,0.20) 50%, rgba(248,164,0,0.04) 92%, rgba(248,164,0,0) 100%)'
          }} data-id="element-21" data-mp="2d0cvzlt8xt12" />
            <span className="relative" data-id="element-22" data-mp="1pclmepz3bu3h">{t.cases.highlight}</span>
          </span>
        </h2>
      </motion.div>

      <ul ref={trackRef} onScroll={updateButtons} aria-label={t.cases.carouselLabel} className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 sm:mt-14" style={{
      paddingLeft: EDGE,
      paddingRight: EDGE,
      scrollPaddingLeft: EDGE
    }} data-id="element-23" data-mp="1fc2v5a575bbj">
        {caseStudies.map(study => <li key={study.id} className="w-[88vw] shrink-0 snap-start sm:w-[600px] lg:w-[755px]" data-id="element-24" data-mp="3eo448wga9hye">
            <CaseStudyCard study={study} data-id="element-25" data-mp="132v8h4inkpdb" />
          </li>)}
      </ul>

      <div className="mx-auto mt-6 flex max-w-[1140px] justify-end gap-3 px-5" data-id="element-26" data-mp="y38weyek9f63">
        <button type="button" onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label={t.cases.prev} className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E8A33A] text-blue-reways-950-lock shadow-[0_3px_0_#C7831F] transition-all hover:bg-[#F2B04A] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-reways-500 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-27" data-mp="1crn17evjfdpa">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-28" data-mp="1vpz9yzijelkc" />
        </button>
        <button type="button" onClick={() => scrollByCard(1)} disabled={!canNext} aria-label={t.cases.next} className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E8A33A] text-blue-reways-950-lock shadow-[0_3px_0_#C7831F] transition-all hover:bg-[#F2B04A] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-reways-500 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-29" data-mp="1qsfxa41hmgui">
          <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-30" data-mp="2vgdkmb9qxnd" />
        </button>
      </div>
    </section>;
}